import { useEffect, useState, useMemo } from "react";
import AdminLayout from "../components/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Wifi,
  Server,
  Users,
  RefreshCw,
  Settings,
  Check,
  X,
  AlertTriangle,
  Plug,
  Search,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Package,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";

export default function AdminMikroTik() {
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [secrets, setSecrets] = useState([]);
  const [activeConnections, setActiveConnections] = useState([]);
  const [addressList, setAddressList] = useState([]);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncingPlans, setSyncingPlans] = useState(false);
  const [settings, setSettings] = useState({
    host: "",
    port: 8728,
    username: "",
    password: "",
    address_list_name: "No-Payment",
  });

  // Search and Sort state for Active Connections
  const [activeSearch, setActiveSearch] = useState("");
  const [activeSortField, setActiveSortField] = useState("name");
  const [activeSortOrder, setActiveSortOrder] = useState("asc");

  // Search and Sort state for Blocked Clients
  const [blockedSearch, setBlockedSearch] = useState("");
  const [blockedSortField, setBlockedSortField] = useState("comment");
  const [blockedSortOrder, setBlockedSortOrder] = useState("asc");

  const [showActiveConnections, setShowActiveConnections] = useState(true);
  const [showBlockedClients, setShowBlockedClients] = useState(true);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };

      // Use timeout for API calls
      const timeout = 10000; // 10 seconds

      const [statusRes, settingsRes] = await Promise.all([
        axios.get(`${API}/mikrotik/status`, { headers, timeout }),
        axios.get(`${API}/mikrotik/settings`, { headers, timeout }),
      ]);

      setStatus(statusRes.data);
      setSettings((prev) => ({ ...prev, ...settingsRes.data }));

      if (statusRes.data.connected) {
        try {
          const [secretsRes, activeRes, addressRes] = await Promise.all([
            axios.get(`${API}/mikrotik/pppoe-secrets`, { headers, timeout }),
            axios.get(`${API}/mikrotik/active-connections`, {
              headers,
              timeout,
            }),
            axios.get(`${API}/mikrotik/address-list`, { headers, timeout }),
          ]);
          setSecrets(secretsRes.data || []);
          setActiveConnections(activeRes.data || []);
          setAddressList(addressRes.data || []);
        } catch (dataError) {
          console.error("Failed to fetch MikroTik data:", dataError);
        }
      }
    } catch (error) {
      console.error("MikroTik connection error:", error);
      setStatus({
        connected: false,
        error: error.message || "Connection timeout",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API}/mikrotik/settings`, settings, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("MikroTik settings saved");
      setSettingsOpen(false);
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save settings");
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(
        `${API}/mikrotik/sync-clients`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      toast.success(res.data.message || "Sync complete");
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const handleSyncPlans = async () => {
    setSyncingPlans(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(
        `${API}/mikrotik/sync-plans`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      toast.success(
        `${res.data.message} - Created: ${res.data.created}, Updated: ${res.data.updated}`,
      );
    } catch (error) {
      toast.error(error.response?.data?.detail || "Plans sync failed");
    } finally {
      setSyncingPlans(false);
    }
  };

  // Parse uptime string to seconds for sorting
  const parseUptime = (uptime) => {
    if (!uptime) return 0;
    let seconds = 0;
    const weeks = uptime.match(/(\d+)w/);
    const days = uptime.match(/(\d+)d/);
    const hours = uptime.match(/(\d+)h/);
    const mins = uptime.match(/(\d+)m/);
    const secs = uptime.match(/(\d+)s/);
    if (weeks) seconds += parseInt(weeks[1]) * 604800;
    if (days) seconds += parseInt(days[1]) * 86400;
    if (hours) seconds += parseInt(hours[1]) * 3600;
    if (mins) seconds += parseInt(mins[1]) * 60;
    if (secs) seconds += parseInt(secs[1]);
    return seconds;
  };

  // Filtered and sorted Active Connections
  const filteredActiveConnections = useMemo(() => {
    let filtered = activeConnections.filter((conn) => {
      const searchLower = activeSearch.toLowerCase();
      return (
        (conn.name || "").toLowerCase().includes(searchLower) ||
        (conn.address || "").toLowerCase().includes(searchLower) ||
        (conn["caller-id"] || "").toLowerCase().includes(searchLower)
      );
    });

    // Sort
    filtered.sort((a, b) => {
      let aVal, bVal;
      if (activeSortField === "uptime") {
        aVal = parseUptime(a.uptime);
        bVal = parseUptime(b.uptime);
      } else {
        aVal = (a[activeSortField] || "").toLowerCase();
        bVal = (b[activeSortField] || "").toLowerCase();
      }

      if (activeSortOrder === "asc") {
        return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
      } else {
        return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
      }
    });

    return filtered;
  }, [activeConnections, activeSearch, activeSortField, activeSortOrder]);

  // Filtered and sorted Blocked Clients
  const filteredBlockedClients = useMemo(() => {
    let filtered = addressList.filter((entry) => {
      const searchLower = blockedSearch.toLowerCase();
      return (
        (entry.comment || "").toLowerCase().includes(searchLower) ||
        (entry.list || "").toLowerCase().includes(searchLower)
      );
    });

    // Sort
    filtered.sort((a, b) => {
      const aVal = (a[blockedSortField] || "").toLowerCase();
      const bVal = (b[blockedSortField] || "").toLowerCase();

      if (blockedSortOrder === "asc") {
        return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
      } else {
        return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
      }
    });

    return filtered;
  }, [addressList, blockedSearch, blockedSortField, blockedSortOrder]);

  // Toggle sort
  const toggleActiveSort = (field) => {
    if (activeSortField === field) {
      setActiveSortOrder(activeSortOrder === "asc" ? "desc" : "asc");
    } else {
      setActiveSortField(field);
      setActiveSortOrder("asc");
    }
  };

  const toggleBlockedSort = (field) => {
    if (blockedSortField === field) {
      setBlockedSortOrder(blockedSortOrder === "asc" ? "desc" : "asc");
    } else {
      setBlockedSortField(field);
      setBlockedSortOrder("asc");
    }
  };

  // Sort icon component
  const SortIcon = ({ field, currentField, currentOrder }) => {
    if (field !== currentField)
      return <ArrowUpDown className="w-4 h-4 text-slate-400" />;
    return currentOrder === "asc" ? (
      <ArrowUp className="w-4 h-4 text-blue-600" />
    ) : (
      <ArrowDown className="w-4 h-4 text-blue-600" />
    );
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">
              MikroTik Integration
            </h1>
            <p className="text-slate-600 mt-1">
              Manage router connection and PPPoE clients
            </p>
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={handleSyncPlans}
              disabled={syncingPlans || !status?.connected}
              data-testid="sync-plans-btn"
            >
              <Package
                className={`w-4 h-4 mr-2 ${syncingPlans ? "animate-spin" : ""}`}
              />
              {syncingPlans ? "Syncing..." : "Sync Plans"}
            </Button>
            <Button
              variant="outline"
              onClick={handleSync}
              disabled={syncing || !status?.connected}
              data-testid="sync-clients-btn"
            >
              <RefreshCw
                className={`w-4 h-4 mr-2 ${syncing ? "animate-spin" : ""}`}
              />
              {syncing ? "Syncing..." : "Sync Clients"}
            </Button>
            <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
              <DialogTrigger asChild>
                <Button>
                  <Settings className="w-4 h-4 mr-2" />
                  Settings
                </Button>
              </DialogTrigger>
              <DialogContent className="p-0 overflow-hidden">
                <DialogHeader className="top-0 z-20 bg-white px-6 py-4 border-b">
                  <DialogTitle>MikroTik Connection Settings</DialogTitle>
                </DialogHeader>
                <div className="px-6 pb-6">
                  <form onSubmit={handleSaveSettings} className="space-y-4">
                    <div>
                      <Label>Host / IP Address</Label>
                      <Input
                        value={settings.host}
                        onChange={(e) =>
                          setSettings({ ...settings, host: e.target.value })
                        }
                        placeholder="192.168.1.1"
                        required
                      />
                    </div>
                    <div>
                      <Label>API Port</Label>
                      <Input
                        type="number"
                        value={settings.port}
                        onChange={(e) =>
                          setSettings({
                            ...settings,
                            port: parseInt(e.target.value),
                          })
                        }
                        placeholder="8728"
                        required
                      />
                    </div>
                    <div>
                      <Label>Username</Label>
                      <Input
                        value={settings.username}
                        onChange={(e) =>
                          setSettings({ ...settings, username: e.target.value })
                        }
                        placeholder="admin"
                        required
                      />
                    </div>
                    <div>
                      <Label>Password</Label>
                      <Input
                        type="password"
                        value={
                          settings.password === "***hidden***"
                            ? ""
                            : settings.password
                        }
                        onChange={(e) =>
                          setSettings({ ...settings, password: e.target.value })
                        }
                        placeholder="Enter new password"
                      />
                      <p className="text-xs text-slate-500 mt-1">
                        Leave empty to keep current password
                      </p>
                    </div>
                    <div>
                      <Label>Address List Name (for blocking)</Label>
                      <Input
                        value={settings.address_list_name}
                        onChange={(e) =>
                          setSettings({
                            ...settings,
                            address_list_name: e.target.value,
                          })
                        }
                        placeholder="No-Payment"
                      />
                    </div>
                    <Button type="submit" className="w-full">
                      Save Settings
                    </Button>
                  </form>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Connection Status Card */}
        <div
          className={`rounded-xl p-6 mb-8 ${status?.connected ? "bg-emerald-50 border border-emerald-200" : "bg-red-50 border border-red-200"}`}
        >
          <div className="flex items-center gap-4">
            <div
              className={`w-12 h-12 rounded-lg flex items-center justify-center ${status?.connected ? "bg-emerald-100" : "bg-red-100"}`}
            >
              {status?.connected ? (
                <Check className="w-6 h-6 text-emerald-600" />
              ) : (
                <X className="w-6 h-6 text-red-600" />
              )}
            </div>
            <div>
              <h2 className="font-semibold text-lg">
                {status?.connected ? "Connected to MikroTik" : "Not Connected"}
              </h2>
              <p className="text-sm text-slate-600">
                {status?.connected
                  ? `Router: ${status.identity} • Host: ${status.host}:${status.port}`
                  : status?.error ||
                    `Unable to connect to ${status?.host}:${status?.port}`}
              </p>
            </div>
          </div>
        </div>

        {!status?.connected && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-8">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
              <div>
                <h3 className="font-semibold text-amber-800">
                  Connection Required
                </h3>
                <p className="text-sm text-amber-700 mt-1">
                  Please configure your MikroTik connection settings to enable
                  PPPoE management and auto-disconnection features.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">
                  {secrets.length}
                </p>
                <p className="text-sm text-slate-500">PPPoE Secrets</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center">
                <Plug className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">
                  {activeConnections.length}
                </p>
                <p className="text-sm text-slate-500">Active Connections</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-red-50 rounded-lg flex items-center justify-center">
                <X className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">
                  {addressList.length}
                </p>
                <p className="text-sm text-slate-500">Blocked (No-Payment)</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center">
                <Server className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">
                  {secrets.length - activeConnections.length}
                </p>
                <p className="text-sm text-slate-500">Offline</p>
              </div>
            </div>
          </div>
        </div>

        {/* Active Connections Table */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm mb-8">
          {/* <div className="p-5 border-b border-slate-100">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <h2 className="font-semibold text-slate-900 flex items-center gap-2">
                <Plug className="w-5 h-5 text-emerald-600" />
                Active PPPoE Connections
                <span className="text-sm font-normal text-slate-500">
                  ({filteredActiveConnections.length} of{" "}
                  {activeConnections.length})
                </span>
              </h2>
              <div className="relative w-full md:w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search username, IP, MAC..."
                  value={activeSearch}
                  onChange={(e) => setActiveSearch(e.target.value)}
                  className="pl-10"
                  data-testid="active-search"
                />
              </div>
            </div>
          </div> */}

          <div className="p-5 border-b border-slate-100">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div
                className="flex items-center gap-2 cursor-pointer select-none"
                onClick={() => setShowActiveConnections(!showActiveConnections)}
              >
                {showActiveConnections ? (
                  <ChevronDown className="w-5 h-5 text-slate-500" />
                ) : (
                  <ChevronRight className="w-5 h-5 text-slate-500" />
                )}

                <Plug className="w-5 h-5 text-emerald-600" />

                <h2 className="font-semibold text-slate-900">
                  Active PPPoE Connections
                </h2>

                <span className="text-sm font-normal text-slate-500">
                  ({filteredActiveConnections.length} of{" "}
                  {activeConnections.length})
                </span>
              </div>

              <div className="relative w-full md:w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />

                <Input
                  placeholder="Search username, IP, MAC..."
                  value={activeSearch}
                  onChange={(e) => setActiveSearch(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
          </div>
          {showActiveConnections && (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th
                      className="px-6 py-3 text-left text-xs font-semibold text-slate-600 cursor-pointer hover:bg-slate-100"
                      onClick={() => toggleActiveSort("name")}
                    >
                      <div className="flex items-center gap-2">
                        Username
                        <SortIcon
                          field="name"
                          currentField={activeSortField}
                          currentOrder={activeSortOrder}
                        />
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-semibold text-slate-600 cursor-pointer hover:bg-slate-100"
                      onClick={() => toggleActiveSort("address")}
                    >
                      <div className="flex items-center gap-2">
                        IP Address
                        <SortIcon
                          field="address"
                          currentField={activeSortField}
                          currentOrder={activeSortOrder}
                        />
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-semibold text-slate-600 cursor-pointer hover:bg-slate-100"
                      onClick={() => toggleActiveSort("uptime")}
                    >
                      <div className="flex items-center gap-2">
                        Uptime
                        <SortIcon
                          field="uptime"
                          currentField={activeSortField}
                          currentOrder={activeSortOrder}
                        />
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-semibold text-slate-600 cursor-pointer hover:bg-slate-100"
                      onClick={() => toggleActiveSort("caller-id")}
                    >
                      <div className="flex items-center gap-2">
                        Caller ID (MAC)
                        <SortIcon
                          field="caller-id"
                          currentField={activeSortField}
                          currentOrder={activeSortOrder}
                        />
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredActiveConnections.length === 0 ? (
                    <tr>
                      <td
                        colSpan="4"
                        className="px-6 py-8 text-center text-slate-500"
                      >
                        {activeSearch
                          ? "No connections match your search"
                          : "No active connections"}
                      </td>
                    </tr>
                  ) : (
                    filteredActiveConnections.map((conn, idx) => (
                      <tr key={idx} className="hover:bg-slate-50">
                        <td className="px-6 py-4 text-sm font-medium text-slate-900">
                          {conn.name}
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-600">
                          {conn.address || "N/A"}
                        </td>
                        <td className="px-6 py-4 text-sm text-emerald-600 font-medium">
                          {conn.uptime || "N/A"}
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-500 font-mono">
                          {conn["caller-id"] || "N/A"}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Blocked Clients (Address List) */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
          <div className="p-5 border-b border-slate-100">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div
                className="flex items-center gap-2 cursor-pointer select-none"
                onClick={() => setShowBlockedClients(!showBlockedClients)}
              >
                {showBlockedClients ? (
                  <ChevronDown className="w-5 h-5 text-slate-500" />
                ) : (
                  <ChevronRight className="w-5 h-5 text-slate-500" />
                )}

                <X className="w-5 h-5 text-red-600" />

                <h2 className="font-semibold text-slate-900">
                  Blocked Clients (No-Payment List)
                </h2>

                <span className="text-sm font-normal text-slate-500">
                  ({filteredBlockedClients.length} of {addressList.length})
                </span>
              </div>

              <div className="relative w-full md:w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />

                <Input
                  placeholder="Search PPPoE user..."
                  value={blockedSearch}
                  onChange={(e) => setBlockedSearch(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
          </div>
          {showBlockedClients && (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th
                      className="px-6 py-3 text-left text-xs font-semibold text-slate-600 cursor-pointer hover:bg-slate-100"
                      onClick={() => toggleBlockedSort("comment")}
                    >
                      <div className="flex items-center gap-2">
                        Comment (PPPoE User)
                        <SortIcon
                          field="comment"
                          currentField={blockedSortField}
                          currentOrder={blockedSortOrder}
                        />
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-semibold text-slate-600 cursor-pointer hover:bg-slate-100"
                      onClick={() => toggleBlockedSort("list")}
                    >
                      <div className="flex items-center gap-2">
                        List
                        <SortIcon
                          field="list"
                          currentField={blockedSortField}
                          currentOrder={blockedSortOrder}
                        />
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-semibold text-slate-600 cursor-pointer hover:bg-slate-100"
                      onClick={() => toggleBlockedSort("disabled")}
                    >
                      <div className="flex items-center gap-2">
                        Status
                        <SortIcon
                          field="disabled"
                          currentField={blockedSortField}
                          currentOrder={blockedSortOrder}
                        />
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredBlockedClients.length === 0 ? (
                    <tr>
                      <td
                        colSpan="3"
                        className="px-6 py-8 text-center text-slate-500"
                      >
                        {blockedSearch
                          ? "No blocked clients match your search"
                          : "No blocked clients"}
                      </td>
                    </tr>
                  ) : (
                    filteredBlockedClients.map((entry, idx) => (
                      <tr key={idx} className="hover:bg-slate-50">
                        <td className="px-6 py-4 text-sm font-medium text-slate-900">
                          {entry.comment || "N/A"}
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-600">
                          {entry.list}
                        </td>
                        <td className="px-6 py-4">
                          <span
                            className={`px-2 py-1 text-xs font-medium rounded-full ${entry.disabled === "true" ? "bg-slate-100 text-slate-600" : "bg-red-100 text-red-700"}`}
                          >
                            {entry.disabled === "true" ? "Disabled" : "Active"}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
